import { Form, Formik } from "formik";
import { MGHeading2, MGHelperText, MGLabel } from "@/components/typography";
import {
  MGDropdown,
  MGDropdownField,
  MGDropdownItem,
  MGDurationPickerField,
  MGTextboxField,
} from "@/components/inputs";
import { MGSwitchField } from "@/components/controls";
import { MGModal, MGModalBody, MGModalFooter, MGModalHeader } from "@/components/modals";
import { MGButton } from "@/components/buttons";
import { MGSpacing } from "@/components/spacing";
import { MGProgressSpinner } from "@/components/progress";
import PropTypes from "prop-types";
import * as yup from "yup";
import UserUtils from "@/app/utils/users/user_utils";
import { useTranslation } from "react-i18next";
import PermissionsService from "@/app/services/permissions/PermissionsService";
import PermissionConstants from "@/app/services/permissions/PermissionConstants";
import { ScreenshareModePicker } from "@/app/app-components/ScreenshareModePicker/ScreenshareModePicker";
import RegexUtils from "@/app/utils/regex/RegexUtils";
import { useState } from "react";

export const ClassStartModal = ({
  isShowing,
  // initialValues,
  classDuration,
  customDuration,
  screenShareMode,
  loading,
  classDurations,
  modes,
  classContent,
  className,
  onSubmit,
  onCancel,
}) => {
  const { t } = useTranslation();

  const validationSchema = yup.object({
    url: yup.string().matches(RegexUtils.URL_PATTERN, t("Please enter a valid URL")),
  });
  const currentSchoolUser = UserUtils.getCurrentSchoolUser();

  const [selectedClassContent, setSelectedClassContent] = useState(null);

  const getForceJoinMeta = () => {
    //Force join is a bit finnicky with regards to User Permission & Global Preferences
    //This function is used to determine its visibilty, editability and if it should receive a default value
    let forceJoin_disabled, forceJoin_forcedOn, forceJoin_Visible;

    //This is the School's Global Preference setting for Force Join, where the school allows starting a Class with force join enabled
    const schoolAllowsForceJoin = currentSchoolUser?.school_device_defaults?.force_class_join;
    //N.B!!!, if this is enabled, force join's DEFAULT setting is "ON" - this will be important later

    //This is a User permission that allows the user to TOGGLE force join.
    const userHasPermissionToForceJoin = PermissionsService.hasPermission(PermissionConstants.ConfigureForceJoin);

    //Global Pref is enabled, and User has permission
    if (schoolAllowsForceJoin && userHasPermissionToForceJoin) {
      forceJoin_disabled = false; //Is not disabled as the User Permission allows toggling
      forceJoin_forcedOn = false; //Does not have a default value as the User Permission allows toggling
      forceJoin_Visible = true; //Should be visible as school allows Force Join setting
    }

    //Global Pref is enabled, but User does not have permission
    if (schoolAllowsForceJoin && !userHasPermissionToForceJoin) {
      forceJoin_disabled = true; //Disabled as the User does not have Permission to toggle
      forceJoin_forcedOn = true; //Global Pref's default setting is "ON", but since the field is disabled, the "ON" setting has to override defaults
      forceJoin_Visible = true; //Should be visible as school allows Force Join setting
    }

    //Global Pref is disabled, this will supercede any effect the User permission has, so it is all we care about
    if (!schoolAllowsForceJoin) {
      forceJoin_Visible = false; //Force join field is not visible, therefore other settings are redundant
      forceJoin_disabled = false;
      forceJoin_forcedOn = false;
    }

    return {
      forceJoin_disabled,
      forceJoin_forcedOn,
      forceJoin_Visible,
    };
  };

  const { forceJoin_disabled, forceJoin_forcedOn, forceJoin_Visible } = getForceJoinMeta();

  const initialValues = {
    url: "", // probably want to inject this
    duration: classDuration,
    bluetooth: false,
    forceJoin: forceJoin_forcedOn ?? false,
    classModeId: null,
    screen_share_enabled: screenShareMode ?? "ON",
    custom_duration: customDuration,
  };

  return (
    <MGModal isShowing={isShowing}>
      {loading && <MGProgressSpinner isLarge={true} />}
      {!loading && (
        <Formik initialValues={initialValues} onSubmit={onSubmit} validationSchema={validationSchema}>
          {({ isSubmitting, isValid, /*status,*/ setFieldValue, setFieldTouched, values }) => {
            const setUrlFromClassContent = async (item) => {
              setSelectedClassContent(item);
              setFieldValue("url", item.meta_url);
              setFieldTouched("url");
            };

            return (
              <Form noValidate autoComplete={"off"}>
                {/* Leaving Status here to show how you can communicate back to form from the submit handler*/}

                {/* <h2 style={{ color: "red" }}>{status && status}</h2> */}
                <MGModalHeader>
                  {className ? <MGHeading2>{className}</MGHeading2> : <MGHeading2>{t("Start Class")}</MGHeading2>}

                  <p>{t("Basic settings before you start your class")}</p>
                </MGModalHeader>
                <MGModalBody>
                  <MGLabel htmlFor="duration">{t("Class Duration")}</MGLabel>

                  <MGDropdownField
                    name="duration"
                    placeholder={t("Duration")}
                    onChange={(v) => {
                      if (v != 0) {
                        setFieldValue("custom_duration", v);
                      } else {
                        setFieldValue("custom_duration", null);
                      }
                    }}
                  >
                    {classDurations.map((duration) => (
                      <MGDropdownItem key={duration.value} value={duration.value}>
                        {duration.content}
                      </MGDropdownItem>
                    ))}
                  </MGDropdownField>
                  {values.duration === 0 && (
                    <>
                      <br />
                      <MGLabel>{t("Custom Class Duration")}</MGLabel>
                      <MGDurationPickerField name="custom_duration" />
                    </>
                  )}

                  <MGLabel htmlFor="url">{t("Student Home Screen")}</MGLabel>
                  <br />
                  <MGTextboxField
                    name={"url"}
                    placeholder="'eg. www.google.com'"
                    onChange={() => {
                      setSelectedClassContent(null);
                    }}
                  ></MGTextboxField>

                  {classContent && classContent.length > 0 && (
                    <>
                      <center style={{ margin: "var(--mg-spacing-sm) 0px" }}>
                        <MGHelperText>{t("or")}</MGHelperText>
                      </center>
                      <div style={{ margin: "var(--mg-spacing-md) 0px" }}>
                        <MGDropdown
                          placeholder={t("Select from class content")}
                          value={selectedClassContent}
                          onChange={setUrlFromClassContent}
                        >
                          {classContent.map((item) => (
                            <MGDropdownItem key={item.id} value={item}>
                              {item.name}
                            </MGDropdownItem>
                          ))}
                        </MGDropdown>
                      </div>
                    </>
                  )}

                  <MGLabel htmlFor="classModeId">{t("Class Mode")}</MGLabel>
                  <MGDropdownField
                    name="classModeId"
                    placeholder={
                      !modes || modes.length < 1 ? t("No class modes available") : t("Please select Class Mode")
                    }
                    disabled={!modes || modes.length < 1}
                  >
                    <MGDropdownItem key={-1} value={""}>
                      <span style={{ color: "var(--mg-color-side-bar-text-selected)" }}>{t("Mode off")}</span>
                    </MGDropdownItem>
                    {modes?.map((mode) => (
                      <MGDropdownItem key={mode.value} value={mode.value}>
                        {mode.content}
                      </MGDropdownItem>
                    ))}
                  </MGDropdownField>

                  {PermissionsService.hasPermission(PermissionConstants.ConfigureBluetooth) && (
                    <MGSwitchField helperText={t("*iOS Only")} name="bluetooth">
                      {t("Bluetooth")}
                    </MGSwitchField>
                  )}

                  {forceJoin_Visible && (
                    <MGSwitchField disabled={forceJoin_disabled} name="forceJoin">
                      {t("Force Join")}
                    </MGSwitchField>
                  )}

                  {UserUtils.getCurrentSchoolUser()?.school?.allow_screen_share_configuration && (
                    <>
                      <MGLabel htmlFor="screen_share_enabled">{t("Screen share mode")}</MGLabel>
                      <ScreenshareModePicker
                        name={"screen_share_enabled"}
                        placeholder={t("Select screen share mode")}
                      />
                      {/* <MGDropdownField name="screen_share_enabled">
                        <MGDropdownItem value="ON">
                          {t("On")} <MGSpacing size={"xxs"} />
                          <MGHelperText>{t("(Chromebook and Android only)")}</MGHelperText>
                        </MGDropdownItem>
                        <MGDropdownItem value="OFF">
                          {t("Off")} <MGSpacing size={"xxs"} />
                          <MGHelperText>{t("(All)")}</MGHelperText>
                        </MGDropdownItem>
                        <MGDropdownItem value="ACTIVETAB">
                          {t("Active Tab")} <MGSpacing size={"xxs"} />
                          <MGHelperText>{t("(Chromebook only)")}</MGHelperText>
                        </MGDropdownItem>
                      </MGDropdownField> */}
                    </>
                  )}
                  <MGModalFooter>
                    <MGButton variant="secondary" onClick={onCancel}>
                      {t("Cancel")}
                    </MGButton>
                    <MGSpacing size={"md"} />

                    <MGButton
                      variant="primary"
                      disabled={
                        isSubmitting ||
                        !isValid ||
                        (!PermissionsService.hasPermission(PermissionConstants.StartClassOutsideSchoolHours) &&
                          !currentSchoolUser.is_in_session)
                      }
                      isLoading={isSubmitting}
                      type={"submit"}
                    >
                      {t("Start Class")}
                    </MGButton>
                  </MGModalFooter>
                </MGModalBody>
              </Form>
            );
          }}
        </Formik>
      )}
    </MGModal>
  );
};

ClassStartModal.propTypes = {
  isShowing: PropTypes.bool,
  initialValues: PropTypes.any, // FIXME: Fixme
  loading: PropTypes.bool,
  classDurations: PropTypes.any, // FIXME: Fixme
  modes: PropTypes.any, // FIXME: Fixme
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default ClassStartModal;
